// src/components/PIIAnonymizerPage/PIIAnonymizationOptionsDialog/OptionsTab.jsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Stack,
    Typography,
    Switch,
    Chip,
    TextField,
    Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const OptionRow = ({
    title,
    description,
    checked,
    onChange,
    extraLabel,
    disabled,
    sx, 
}) => (
    <Box
        sx={{
            mb: 1.5,
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            ...sx,
        }}
    >
        <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1" noWrap>
                    {title}
                </Typography>
                {extraLabel}
            </Stack>
            {description && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.25 }}
                >
                    {description}
                </Typography>
            )}
        </Box>
        <Switch
            edge="end"
            checked={checked || false}
            onChange={onChange}
            disabled={disabled}
        />
    </Box>
);

const OptionsTab = ({ options, onToggle, onChangeField, loading, pseudoAnonymization }) => {
    const { t } = useTranslation();

    const customWords = Array.isArray(options.custom_words)
        ? options.custom_words
        : [];

    const [rawInputValue, setRawInputValue] = useState(() => customWords.join(', '));

    useEffect(() => {
        const incomingDataStr = customWords.join(', ');
        const currentDerivedStr = rawInputValue
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0)
            .join(', ');

        if (incomingDataStr !== currentDerivedStr) {
            setRawInputValue(incomingDataStr);
        }
    }, [options.custom_words]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCustomWordsChange = (event) => {
        const raw = event.target.value || '';
        
        setRawInputValue(raw);

        const parts = raw
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
            
        onChangeField('custom_words')(parts);
    };

    return (
        <Box>
            {/* SECTION: what to anonymize */}
            <Typography
                variant="overline"
                color="text.secondary"
                sx={{ mb: 1, display: 'block' }}
            >
                {t(
                    'admin.pii.section.what',
                    'Aké údaje anonymizovať'
                )}
            </Typography>

            {/* Hide during pseudo anonymization */}
            {!pseudoAnonymization && (
                <OptionRow
                    title={t(
                        'admin.pii.opt.saveToDb',
                        'Uložiť súbory do DB'
                    )}
                    description={t(
                        'admin.pii.opt.saveToDb.desc',
                        'Uložiť pôvodný aj anonymizovaný súbor do DB.'
                    )}
                    checked={options.save_to_db}
                    onChange={onToggle('save_to_db')}
                    disabled={loading}
                />
            )}

            <OptionRow
                title={t(
                    'admin.pii.opt.anonymizeBirthNumber',
                    'Anonymizovať rodné čísla (RČ)'
                )}
                description={t(
                    'admin.pii.opt.anonymizeBirthNumber.desc',
                    'Anonymizovať rodné čísla.'
                )}
                checked={options.anonymize_birth_number}
                onChange={onToggle('anonymize_birth_number')}
                disabled={loading}
            />

            {/* MAIN NAME TOGGLE */}
            <OptionRow
                title={t(
                    'admin.pii.opt.anonymizeNames',
                    'Anonymizovať mená a priezviská'
                )}
                description={t(
                    'admin.pii.opt.anonymizeNames.desc',
                    'Anonymizovať mená a priezviská.'
                )}
                checked={options.anonymize_names}
                onChange={onToggle('anonymize_names')}
                disabled={loading}
            />

            {/* SUBRULES FOR NAMES */}
            <Box sx={{ ml: 4, mb: 3, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
                
                {/* 1. Exceptions (First Page) */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 'bold', textTransform: 'uppercase' }}>
                        {t('admin.pii.opt.namesExceptions', 'Výnimky')}
                    </Typography>
                    <OptionRow
                        title={t(
                            'admin.pii.opt.excludeContractInfo',
                            'Vylúčiť zmluvné/kontaktné info (1. strana)'
                        )}
                        description={t(
                            'admin.pii.opt.excludeContractInfo.desc',
                            'Vylúčiť zmluvné/kontaktné informácie na prvej normostrane.'
                        )}
                        checked={options.exclude_contract_info_first_page}
                        onChange={onToggle('exclude_contract_info_first_page')}
                        disabled={loading || !options.anonymize_names}
                        sx={{ mb: 0, bgcolor: 'transparent', p: 0 }} 
                    />
                </Box>

                <Divider sx={{ my: 2, opacity: 0.6 }} />

                {/* 2. Model Sensitivity (Threshold) */}
                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 'bold', textTransform: 'uppercase' }}>
                        {t('admin.pii.opt.namesSensitivity', 'Citlivosť modelu (Celý dokument)')}
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={t(
                            'admin.pii.opt.nameConfidenceThreshold',
                            'Prahová hodnota spoľahlivosti (%)'
                        )}
                        value={options.name_confidence_threshold ?? 60}
                        onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            onChangeField('name_confidence_threshold')(isNaN(val) ? 60 : val);
                        }}
                        disabled={loading || !options.anonymize_names}
                        inputProps={{ min: 0, max: 100, step: 1 }}
                        helperText={t(
                            'admin.pii.opt.nameConfidenceThreshold.help',
                            'Nižšia hodnota zachytí viac mien (aj neisté), vyššia hodnota anonymizuje len slová s vysokou istotou modelu.'
                        )}
                    />
                </Box>
            </Box>

            <OptionRow
                title={t(
                    'admin.pii.opt.anonymizeBirthDate',
                    'Anonymizovať dátumy narodenia'
                )}
                description={t(
                    'admin.pii.opt.anonymizeBirthDate.desc',
                    'Anonymizovať dátumy narodenia.'
                )}
                checked={options.anonymize_birth_date}
                onChange={onToggle('anonymize_birth_date')}
                disabled={loading}
            />

            {/* NEW: Passport Number */}
            <OptionRow
                title={t(
                    'admin.pii.opt.anonymizePassportNumber',
                    'Anonymizovať čísla pasov'
                )}
                description={t(
                    'admin.pii.opt.anonymizePassportNumber.desc',
                    'Anonymizovať čísla cestovných pasov.'
                )}
                checked={options.anonymize_passport_number}
                onChange={onToggle('anonymize_passport_number')}
                disabled={loading}
            />

            {/* VIN a EČV (#168, 23. 9. 2026) — chýbajúca hodnota = zapnuté */}
            <OptionRow
                title={t(
                    'admin.pii.opt.anonymizeVehicle',
                    'Anonymizovať údaje o vozidle'
                )}
                description={t(
                    'admin.pii.opt.anonymizeVehicle.desc',
                    'VIN (identifikačné číslo vozidla) a evidenčné číslo vozidla (EČV).'
                )}
                checked={options.anonymize_vehicle !== false}
                onChange={onToggle('anonymize_vehicle')}
                disabled={loading}
            />

            {/* NEW: ID Number */}
            <OptionRow
                title={t(
                    'admin.pii.opt.anonymizeIdNumber',
                    'Anonymizovať čísla OP / ID'
                )}
                description={t(
                    'admin.pii.opt.anonymizeIdNumber.desc',
                    'Anonymizovať čísla občianskych preukazov a ID kariet.'
                )}
                checked={options.anonymize_id_number}
                onChange={onToggle('anonymize_id_number')}
                disabled={loading}
            />

            <OptionRow
                title={t(
                    'admin.pii.opt.anonymizeAccountNumber',
                    'Anonymizovať čísla účtov'
                )}
                description={t(
                    'admin.pii.opt.anonymizeAccountNumber.desc',
                    'Anonymizovať čísla účtov.'
                )}
                checked={options.anonymize_account_number}
                onChange={onToggle('anonymize_account_number')}
                disabled={loading}
            />

            <OptionRow
                title={t(
                    'admin.pii.opt.anonymizeAddress',
                    'Anonymizovať adresy'
                )}
                description={t(
                    'admin.pii.opt.anonymizeAddress.desc',
                    'Anonymizovať adresy.'
                )}
                checked={options.anonymize_address}
                onChange={onToggle('anonymize_address')}
                disabled={loading}
            />

            <OptionRow
                title={t(
                    'admin.pii.opt.anonymizePhone',
                    'Anonymizovať telefónne čísla'
                )}
                description={t(
                    'admin.pii.opt.anonymizePhone.desc',
                    'Anonymizovať telefónne čísla.'
                )}
                checked={options.anonymize_phone}
                onChange={onToggle('anonymize_phone')}
                disabled={loading}
            />

            <OptionRow
                title={t(
                    'admin.pii.opt.anonymizeEmail',
                    'Anonymizovať e-mailové adresy'
                )}
                description={t(
                    'admin.pii.opt.anonymizeEmail.desc',
                    'Anonymizovať e-mailové adresy.'
                )}
                checked={options.anonymize_email}
                onChange={onToggle('anonymize_email')}
                disabled={loading}
            />

            {/* Hide during pseudo anonymization */}
            {!pseudoAnonymization && (
                <OptionRow
                    title={t(
                        'admin.pii.opt.anonymizeSignature',
                        'Anonymizovať podpisy'
                    )}
                    description={t(
                        'admin.pii.opt.anonymizeSignature.desc',
                        'Anonymizovať podpisy (beta).'
                    )}
                    checked={options.anonymize_signature}
                    onChange={onToggle('anonymize_signature')}
                    disabled={loading}
                    extraLabel={
                        <Chip
                            size="small"
                            label={t('common.beta', 'Beta')}
                            color="warning"
                            variant="outlined"
                        />
                    }
                />
            )}

            <TextField
                fullWidth
                size="small"
                label={t(
                    'admin.pii.opt.customWords.label',
                    'Vlastné slová / frázy na anonymizáciu'
                )}
                placeholder={t(
                    'admin.pii.opt.customWords.placeholder',
                    'Napr. "Janko, Firma XYZ"'
                )}
                value={rawInputValue}
                onChange={handleCustomWordsChange}
                disabled={loading}
                helperText={t(
                    'admin.pii.opt.customWords.help',
                    'Zadaj ako zoznam oddelený čiarkou. Každá hodnota bude anonymizovaná podobne ako ostatné osobné údaje.'
                )}
                sx={{ mt: 0.5 }}
            />
        </Box>
    );
};

export default OptionsTab;